// ─────────────────────────────────────────────
//  EditDepartStep — "Edit CRNA" inside the quick-actions menu: the panel that
//  changes WHEN this person leaves the room they are standing in, and nothing
//  else about them.
//
//  ⚠️ IT EDITS A TIME, NOT A PERSON, AND THE COPY HAS TO SAY SO. "Edit CRNA" is
//  the owner's phrase for the menu entry and it stays his; every line inside
//  this panel names the actual consequence, because a screen titled with
//  somebody's name that silently rewrites one field is how an operator comes to
//  believe it rewrites more. The write behind it is a ONE-FIELD patch
//  (`secondProviderWrite.setDepartPatch`) for exactly the same reason.
//
//  ⚠️ EMPTY MEANS EMPTY. A row with no recorded leaving time opens with a blank
//  field, never with the derived departure the chart prints — that number
//  usually comes from a shift code or from today's roster, and offering it here
//  would turn a guess into a typed fact the moment it was saved. Clearing is
//  therefore a real action, and has its own button.
//
//  ⚠️ THE PICK IS THE SAVE — there is no separate Save button, deliberately.
//  `TimeCombo` commits typed text on a 120ms blur timer, so a Save button
//  pressed straight after typing would fire BEFORE the value arrived and write
//  the old one. Committing on change removes the race outright, and it matches
//  `PickStep`, where choosing is likewise the whole step: "a second question is
//  a second chance to put somebody in the wrong OR."
// ─────────────────────────────────────────────
import React from "react";
import TimeCombo from "../TimeCombo";
import { DEPART_SHIFT_END_TIMES } from "../../domain/schedule";
import st from "./benchAssignStyles";
import tokens from "../../theme/tokens";

const { color, font } = tokens;

// ⚠️ THE FIVE TIMES THIS DEPARTMENT LEAVES AT, not every quarter hour. The field
// used to offer the full 06:00–23:00 list — 69 options for five real answers —
// and the owner named the real ones on 2026-08-20: *"leaving time: 3 pm, 5 pm,
// 7 pm, 9 pm, 7 am (24h)"*. They are the ends of shift codes 3, 5, 7, 9 and 24,
// so the value written here is the same vocabulary the sheet prints and the
// roster stores, and it is still on the 15-minute grid the room modal, the
// assignment grid and the timeline drag can all express.
//
// An already-stored value outside the list — an import, or an older edit, or a
// genuine one-off — is added back at the FRONT so the field displays what the
// row actually holds instead of silently reading as empty. Same trick
// `assignmentRow.cells` plays, and it is what stops this list from quietly
// deleting a time somebody meant.
export function departOptions(current) {
  const v = String(current || "").trim();
  if (!v || DEPART_SHIFT_END_TIMES.includes(v)) return DEPART_SHIFT_END_TIMES;
  return [v, ...DEPART_SHIFT_END_TIMES];
}

export default function EditDepartStep({
  who,
  roomLabel,
  // "" when the row records no leaving time for this person.
  value = "",
  // (departTime) => void. "" clears the field.
  onSave,
  onBack,
  onCancel,
  busy = false,
}) {
  const current = String(value || "").trim();

  return (
    <>
      <div style={st.title}>When {who} leaves {roomLabel}</div>
      <div style={st.sub}>
        This changes the leaving time only — {who} stays in {roomLabel}, and their
        shift, role and everything else about them are untouched.
      </div>

      {/* A DIV, NOT A <label>. `TimeCombo` carries its own `aria-label` naming
          both the person and the room, which is the useful one; a wrapping
          label would put a second, vaguer name on the same control. */}
      <div style={s.field}>
        <span style={s.label} aria-hidden="true">Leaving time</span>
        <TimeCombo
          value={current}
          options={departOptions(current)}
          onChange={(next) => {
            const v = String(next || "").trim();
            // Nothing changed — a click into the field and back out is not an
            // edit, and must not fire a write or a toast.
            if (v === current) return;
            onSave(v);
          }}
          placeholder="Not recorded"
          ariaLabel={`${who} leaving time in ${roomLabel}`}
          disabled={busy}
        />
      </div>

      <div style={s.hint}>
        {current
          ? "Picking a time saves it straight away."
          : "No leaving time is recorded for them. Picking one saves it straight away."}
      </div>

      <div style={st.actions}>
        {current && (
          // ⚠️ CLEARING IS AN ACTION, NOT AN ACCIDENT. "They leave at 3 PM" and
          // "nobody has said when they leave" are different clinical statements,
          // and the second one has to be reachable — an operator who typed a
          // wrong time otherwise has no way back to the truth.
          <button
            type="button"
            style={{ ...st.ghost, marginRight: "auto" }}
            onClick={() => onSave("")}
            disabled={busy}
          >
            Clear leaving time
          </button>
        )}
        <button type="button" style={st.ghost} onClick={onBack} disabled={busy}>← Back</button>
        <button type="button" style={st.ghost} onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </>
  );
}

const s = {
  field: { display: "block" },
  label: {
    display: "block", fontSize: font.size.label, fontWeight: font.weight.bold,
    color: color.text.secondary, marginBottom: 6,
  },
  hint: { fontSize: 12, color: color.text.muted, marginTop: 8 },
};
